<script setup lang="ts">
import { useRouter, useRoute } from 'vue-router'
import { formatDate, formatDateTime } from '../../utils/format'
import { useRestData } from '../../composables/useRestData'
import { useApi } from '../../composables/api'
import { moderationListFilters } from '../../utils/moderation-list-route'
import CrudDataTable from '../../components/CrudDataTable.vue'
import LocationPicker from '../../components/forms/LocationPicker.vue'
import { formatIncidentType } from '../../utils/format'
import { IncidentReportType, ModerationStatus, type Bounty } from '@roadfootage/api-client'

const { adminBountiesApi } = useApi()
const router = useRouter()
const route = useRoute()

const bountyData = useRestData((params) => adminBountiesApi.adminBountiesPaginate(params), {
  initialPageSize: 25,
  defaultSort: [{ sortBy: 'createdAt', descending: true }]
})

Object.assign(bountyData.activeFilters.value, moderationListFilters(route.query, new Date()))

bountyData.fetch()

const columns = [
  {
    name: 'incidentReportType',
    field: 'incidentReportType',
    label: 'Type',
    align: 'left',
    sortable: true,
    format: (val: string) => formatIncidentType(val)
  },
  {
    name: 'moderationStatus',
    field: 'moderationStatus',
    label: 'Status',
    align: 'left',
    sortable: true
  },
  {
    name: 'createdAt',
    field: 'createdAt',
    label: 'Submitted',
    align: 'left',
    sortable: true,
    format: (val: string) => (val ? formatDate(val) : '')
  },
  {
    name: 'windowStartDateTime',
    field: 'windowStartDateTime',
    label: 'Window Start',
    align: 'left',
    sortable: true,
    format: (val: string) => (val ? formatDateTime(val) : 'N/A')
  },
  {
    name: 'formattedAddress',
    field: (r: Bounty) => r.address?.formattedAddress,
    label: 'Location',
    align: 'left',
    sortable: false,
    format: (val: string) => (val ? val.substring(0, 50) + '…' : '')
  },
  {
    name: 'rewardAmountPoints',
    field: 'rewardAmountPoints',
    label: 'Reward',
    align: 'left',
    sortable: true,
    format: (val: number) => (val !== undefined ? `${val.toLocaleString()} pts` : '')
  },
  {
    name: 'expiresAt',
    field: 'expiresAt',
    label: 'Expires',
    align: 'left',
    sortable: true,
    format: (val: string) => (val ? formatDate(val) : '')
  },
  {
    name: 'isClaimed',
    field: (r: Bounty) => (r.isClaimed ? 'Yes' : 'No'),
    label: 'Claimed',
    align: 'left',
    sortable: false
  },
  { name: 'creator', label: 'Creator', align: 'right', sortable: false },
  { name: 'actions', field: 'actions', label: 'Actions', align: 'right' }
]
</script>

<template>
  <q-page>
    <CrudDataTable
      title="Bounties"
      :columns="columns"
      :rest-data="bountyData"
      :update-api="(params) => adminBountiesApi.adminBountiesUpdateById(params)"
      :delete-api="(params) => adminBountiesApi.adminBountiesDeleteById(params)"
      enable-filters
      enable-multi-sort
      enable-search
    >
      <template #update-form="{ item }">
        <div class="row q-col-gutter-md">
          <div class="col-12 col-md-6">
            <q-select
              v-model="item.moderationStatus"
              :options="Object.values(ModerationStatus)"
              label="Moderation Status"
              outlined
            />
          </div>
          <div class="col-12 col-md-6">
            <q-select
              v-model="item.incidentReportType"
              :options="Object.values(IncidentReportType)"
              label="Footage Type"
              outlined
            />
          </div>
          <div class="col-12 col-md-6">
            <q-input
              :model-value="
                item.windowStartDateTime ? formatDateTime(item.windowStartDateTime) : ''
              "
              label="Window Start"
              outlined
              readonly
            />
          </div>
          <div class="col-12 col-md-6">
            <q-input
              :model-value="item.windowEndDateTime ? formatDateTime(item.windowEndDateTime) : ''"
              label="Window End"
              outlined
              readonly
            />
          </div>
          <div class="col-12">
            <LocationPicker
              :location="item.location"
              :address="item.address"
              @update:location="item.location = $event"
              @update:address="item.address = $event"
            />
          </div>
          <div class="col-12">
            <q-input
              v-model="item.requestDescription"
              label="Request Description"
              type="textarea"
              outlined
            />
          </div>
          <div v-if="item.additionalInformation" class="col-12">
            <q-input
              v-model="item.additionalInformation"
              label="Additional Information"
              type="textarea"
              outlined
            />
          </div>
          <div class="col-12 col-md-4">
            <q-input
              :model-value="`${item.rewardAmountPoints?.toLocaleString() ?? 0} pts`"
              label="Reward"
              outlined
              readonly
            />
          </div>
          <div class="col-12 col-md-4">
            <q-input
              :model-value="item.expiresAt ? formatDate(item.expiresAt) : ''"
              label="Expires"
              outlined
              readonly
            />
          </div>
          <div class="col-12 col-md-4">
            <q-input
              :model-value="
                item.isClaimed ? `Yes — ${item.claimedAt ? formatDate(item.claimedAt) : ''}` : 'No'
              "
              label="Claimed"
              outlined
              readonly
            />
          </div>
        </div>
      </template>

      <template #body-cell-creator="scope">
        <q-td :props="scope">
          <q-btn
            label="Creator"
            color="secondary"
            size="sm"
            @click="router.push({ name: 'users', query: { id: scope.row.userProfileId } })"
          />
        </q-td>
      </template>
    </CrudDataTable>
  </q-page>
</template>
